import {
  Button,
  CustomLabel,
  EasyModal,
  FormTabSwitcher,
  Input,
  Select,
  SimpleAccordion,
  Skeleton,
  TextArea,
  UploadButton,
} from '@ui';
import React, { FC, ReactNode, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import Icon from '@app/components/ui/icons';

import {
  GptGroupMutationObjectType,
  GPTGroupObjectType,
  IndividualGptMetricType,
} from '@app/interfaces/gpt-metric.type';

import { GPTAllIntegrationListObjectType } from '@app/interfaces/settings.type';

import { OptionItem } from '@app/components/ui/select/select.type';

import {
  AllChanelsArrType,
  MetricListArrType,
} from '@app/interfaces/pages-types/anatylics-metric.type';

import { useTranslation } from '@app/hooks/use-translation';

import {
  useCreateGptGroupMutation,
  useCreateProjectMetricGroupForGptMutation,
  useEditGptGroupMutation,
  useGetGptGroupsWithMetricsQuery,
  useLazyTestCalcGptMetricsInGroupQuery,
} from '@app/store/api/gpt-metric.api';

import { useOutletContext, useParams } from 'react-router';

import { MetricTemplatesGroupType } from '@app/interfaces/slices-types/metric-templates.type';

import { AnalyticPageOutlet } from '@app/pages/analytics/single-analytic.page';

import { AllStatusProject } from '@app/interfaces/analytics';

import { useCreateProjectMetricGroupForChatGptMutation } from '@app/store/api/chat-api/gpt-metric-chat.api';

import { HoverButton } from '../../hoverButton';

import AiMetricTestingModal from './blocks/ai-metric-testing-modal';
import GridWrapper from './blocks/grid-wrapper';
import { AiMetricIndividualMetric } from './blocks/ai-metric-individual-metric';

type FormType = Omit<GptGroupMutationObjectType, 'project_id'>;

type AiMetricPropsType = {
  gptModelsList: GPTAllIntegrationListObjectType | undefined;
  allChannels?: AllChanelsArrType[];
  editModal?: boolean;
  copyModal?: boolean;
  gptModalData: GPTGroupObjectType | undefined;
  modalsSetState: (type: string) => void;
  currentTemplate: 'chat' | 'voice';
  metricList?: MetricListArrType[];
  metricTemplates: MetricTemplatesGroupType[] | undefined;
};

const AiMetricVoice: FC<AiMetricPropsType> = (props) => {
  const {
    gptModelsList,
    allChannels,
    editModal,
    gptModalData,
    modalsSetState,
    currentTemplate,
    copyModal,
    metricList,
    metricTemplates,
  } = props;
  const { t: tf } = useTranslation('common.forms');
  const { t } = useTranslation('pages.metricTemplates');
  const { id } = useParams();
  const { status } = useOutletContext<AnalyticPageOutlet>();

  const project_id = id || '';
  // api
  const [testMetric, { currentData }] = useLazyTestCalcGptMetricsInGroupQuery();
  const { data: gptGroupsList } = useGetGptGroupsWithMetricsQuery(
    { params: { project_id } },
    { skip: !project_id },
  );
  const { data: currentGroupMetricsList, isLoading } = useGetGptGroupsWithMetricsQuery(
    { params: { project_id, gpt_group_id: gptModalData?.gpt_group_id } },
    { skip: !project_id || !gptModalData?.gpt_group_id },
  );
  const currentMetricsDataFromGroup = currentGroupMetricsList?.gpt_groups?.[0].gpt_metrics;

  const [createGptGroup] = useCreateGptGroupMutation();
  const [editGptGroup] = useEditGptGroupMutation();

  const [createMetricVoice] = useCreateProjectMetricGroupForGptMutation();
  const [createMetricChat] = useCreateProjectMetricGroupForChatGptMutation();
  const createMetric = { chat: createMetricChat, voice: createMetricVoice }[currentTemplate];
  // api

  // FORM DATA
  const modelsOptions: OptionItem[] = [
    ...(gptModelsList?.default_gpt_integrations.map((integ) => ({
      title: integ.description,
      value: integ.default_integration_id,
    })) || []),
    ...(gptModelsList?.gpt_integrations.map((integ) => ({
      title: integ.description,
      value: integ.gpt_integration_id,
    })) || []),
  ];
  const isDefaultModel =
    gptModelsList?.default_gpt_integrations.find((model) => model.is_default === true)
      ?.default_integration_id ||
    gptModelsList?.gpt_integrations.find((model) => model.is_default === true)?.gpt_integration_id;
  // FORM DATA
  // testing modal

  const [testModalLoading, setTestModalLoading] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [testModalOpenState, setTestModalOpenState] = useState(false);
  const [hideSettingsModal, setHideSettingsModal] = useState(testModalOpenState);
  function onCloseModal() {
    setTestModalOpenState(false);
    setHideSettingsModal(false);
    setStep(1);
  }
  function handleOpenTestModal() {
    setTestModalOpenState(true);
    setTimeout(() => setHideSettingsModal(true), 100);
  }
  const testModalLabelDuringLoading = (
    <div className={'flex items-center flex-col mb-[32px] gap-[20px] w-full justify-center'}>
      <Icon size={57} name={'summspeechduration'} />
      <div className="w-full text-center">
        Процесс тестирования
        <HoverButton
          helperText={
            'Режим тестирования позволяет посмотреть результат работы группы GPT-метрик на конкретной записи.'
          }
        />
      </div>
    </div>
  );
  const testingModalLabels: Record<typeof step, ReactNode> = {
    1: (
      <div className={'flex items-center flex-col mb-[32px] gap-[20px] w-full justify-center'}>
        <Icon size={57} name={'summspeechduration'} />
        <div className="w-full text-center">
          Тестирование Группы GPT-метрик
          <HoverButton
            helperText={
              'Режим тестирования позволяет посмотреть результат работы группы GPT-метрик на конкретной записи.'
            }
          />
        </div>
      </div>
    ),
    2: (
      <div className={'flex items-center flex-col mb-[32px] gap-[20px] w-full justify-center'}>
        <Icon size={57} name={'summspeechduration'} />
        <div className="w-full text-center">
          <div>
            Тестирование группы GPT-метрик
            <HoverButton
              helperText={
                'Режим тестирования позволяет посмотреть результат работы группы GPT-метрик на конкретной записи.'
              }
            />
          </div>
          <div className="text-[14px] font-medium text-4color leading-[109%] tracking-[-0.03em] ">
            Результат работы LLM-модели:{' '}
            {typeof currentData?.result?.raw_response !== 'string'
              ? currentData?.result?.raw_response?.usage?.total_tokens
              : ''}
            токена
          </div>
        </div>
      </div>
    ),
  };
  // testing modal

  const {
    control,
    watch,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<FormType>({
    defaultValues:
      editModal || copyModal
        ? {
            name: `${gptModalData?.name || ''} ${copyModal ? t('copy') : ''}`.trim(),
            settings: { description: gptModalData?.description, settings: gptModalData?.settings },
          }
        : {
            settings: {
              settings: {
                autostart: true,
                channel: 'all',
                gpt_integration: {
                  gpt_integration_id:
                    isDefaultModel ||
                    gptModelsList?.gpt_integrations?.[gptModelsList?.gpt_integrations?.length - 1]
                      ?.gpt_integration_id,
                  type: 'user',
                },
                exclude_begin_sec: 0,
                exclude_end_sec: 0,
                search_begin_sec: 0,
                search_end_sec: 0,
              },
            },
          },
  });

  const [accordionOpenedIds, setIds] = useState<string[]>(['GPT_METRIC_SETTINGS_GROUP']);
  function handleClickAccordion(id: string) {
    setIds((prev) => {
      return prev.includes(id) ? prev.filter((ids) => ids !== id) : [...prev, id];
    });
  }

  function onSubmit(data: FormType) {
    delete data?.['manual_metrics'];
    try {
      editModal && gptModalData?.gpt_group_id
        ? editGptGroup({
            body: data,
            params: { gpt_group_id: gptModalData?.gpt_group_id },
          })
        : copyModal
        ? createGptGroup({ ...data, project_id })
            .unwrap()
            .then((createdProject) => {
              for (const metric of currentMetricsDataFromGroup || []) {
                createMetricHandler({
                  ...metric,
                  name: metric.name + ' Копия',
                  settings: { ...metric.settings, gpt_group: createdProject.gpt_group_id },
                });
              }
            })
        : createGptGroup({ ...data, project_id });
    } finally {
      modalsSetState('gpt');
    }
  }

  function createMetricHandler(
    target:
      | ({
          metric_id: string;
        } & IndividualGptMetricType)
      | undefined,
  ) {
    if (!target) return;
    createMetric({ params: { project_id }, body: target });
  }

  const hiddenButRef = useRef<HTMLButtonElement | null>(null);

  return (
    <div className="relative px-[24px] ">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div
          className={` ${hideSettingsModal ? 'hidden h-auto transition-all duration-[2000]' : ''} `}
        >
          <div className="flex flex-col gap-[32px]">
            <SimpleAccordion
              label={'Настройка группы GPT-метрик'}
              id="GPT_METRIC_SETTINGS_GROUP"
              opened={accordionOpenedIds.includes('GPT_METRIC_SETTINGS_GROUP')}
              onChange={handleClickAccordion}
            >
              <GridWrapper>
                {/* GRID STart*/}
                <div className="flex flex-col gap-[18px]">
                  {/* 1 */}
                  <span className="font-[700] text-[24px] text-0color"></span>
                  {/* 1 */}
                  {/* 2 */}
                  <div>
                    <Input
                      {...register('name', { required: { value: true, message: tf('required') } })}
                      placeholder="Введите текст"
                      label="Название группы обработки"
                      error={errors.name?.message}
                    />
                  </div>
                  {/* 2 */}
                  {/* 3 */}
                  <div className=" grid grid-rows-1 grid-cols-2 ">
                    <div className="flex flex-col gap-[6px] relative">
                      <CustomLabel label="Канал данных" />
                      <Controller
                        render={({ field }) => (
                          <div className="flex items-center flex-wrap gap-x-[32px]">
                            {[
                              { name: 'Все', settings_channels_id: 'all', channel: 99999 },
                              ...(allChannels || []),
                            ].map((channel) => (
                              <div
                                key={channel.settings_channels_id}
                                className="flex items-center gap-[8px] py-[8px]"
                              >
                                <input
                                  name="channel"
                                  type="checkbox"
                                  onChange={(e) => {
                                    field.onChange(e.target.value);
                                  }}
                                  checked={field.value === channel.settings_channels_id}
                                  value={channel.settings_channels_id}
                                  className="focus:ring-offset-0 focus:ring-0 rounded-full"
                                />
                                <span className="font-[500] leading-[109%] text-[13px] tracking-[-0.03em] text-2color">
                                  {channel.name}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        control={control}
                        name="settings.settings.channel"
                        rules={{ validate: (value) => !!value || tf('required') }}
                      />
                      {errors.settings?.settings?.channel?.message && (
                        <div
                          className={
                            'absolute bottom-[-17px] right-[20px] text-sm text-red-600 dark:text-red-500'
                          }
                        >
                          {errors.settings?.settings?.channel?.message}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-[6px]">
                      <CustomLabel label="Модель GPT" />
                      <Controller
                        render={({ field }) => (
                          <div>
                            <Select
                              defaultValue={field.value}
                              onChange={(value) => {
                                field.onChange(value);
                                const type: 'user' | 'system' =
                                  gptModelsList?.default_gpt_integrations.find(
                                    (integ) => integ.default_integration_id === value,
                                  )
                                    ? 'system'
                                    : 'user';
                                setValue('settings.settings.gpt_integration.type', type);
                              }}
                              options={modelsOptions}
                              error={
                                errors.settings?.settings?.gpt_integration?.gpt_integration_id
                                  ?.message
                              }
                            />
                          </div>
                        )}
                        control={control}
                        rules={{ required: { value: true, message: tf('required') } }}
                        name="settings.settings.gpt_integration.gpt_integration_id"
                      />
                    </div>
                  </div>
                  {/* 3 */}
                  {/* 4 */}
                  <div>
                    <TextArea
                      defaultValue={watch('settings.settings.prompt')}
                      onChange={(event) => setValue('settings.settings.prompt', event.target.value)}
                      popupModal
                      height={106}
                      placeholder="Введите ваш промпт"
                      label="Общий промпт"
                    />
                  </div>
                  {/* 4 */}
                </div>
                {/* GRID END */}

                {/* GRID START*/}
                <div className="flex flex-col gap-[18px]">
                  {/* 2 */}
                  <div className="flex flex-col gap-[6px]">
                    <CustomLabel label="Параметры запуска" />
                    <FormTabSwitcher
                      valuesList={[
                        { title: 'Автоматически', value: 'auto' },
                        { title: 'Вручную', value: 'manual' },
                      ]}
                      isActive={watch('settings.settings.autostart') ? 'auto' : 'manual'}
                      onChange={(value) =>
                        setValue('settings.settings.autostart', value === 'auto')
                      }
                    />
                  </div>
                  {/* 2 */}
                  {/* 3 */}
                  <div className="flex flex-col gap-[6px]">
                    <CustomLabel label="Привязать общий файл" />
                    <div>
                      <UploadButton
                        description={`Максимальный размер 10 MB./nФорматы DOCX, DOC, TXT, PDF./nНе поддерживаем PNG.`}
                        loadedFiles={[]}
                        changeLoadedFiles={() => console.log('tt')}
                        descriptionPosition="right"
                        soon
                      />
                    </div>
                  </div>
                  {/* 3 */}
                  {/* 4 */}
                  {/* <div className="flex flex-col gap-[6px]">
                    <CustomLabel label="Удаление персональных данных" />
                    <div className="flex items-center gap-[8px]">
                      <Checkbox
                        label="Имя"
                        className="w-[16px] h-[16px]"
                        onChange={() => console.log('touch')}
                      />
                      <Checkbox
                        label="Телефон"
                        className="w-[16px] h-[16px]"
                        onChange={() => console.log('touch')}
                      />
                      <Checkbox
                        label="Email"
                        className="w-[16px] h-[16px]"
                        onChange={() => console.log('touch')}
                      />
                      <Checkbox
                        label="Паспорт"
                        className="w-[16px] h-[16px]"
                        onChange={() => console.log('touch')}
                      />
                      <Checkbox
                        label="Адрес"
                        className="w-[16px] h-[16px]"
                        onChange={() => console.log('touch')}
                      />
                    </div>
                    <div className="flex items-center gap-[6px]">
                      <Icon size={14} className="text-warning_orange" name="TriangleWarningIcon" />
                      <span className="text-[13px] font-[700] text-warning_orange leading-[109%] tracking-[-0.03em]">
                        Модель GPT будет видеть личные данные
                      </span>
                    </div>
                  </div> */}
                  {/* 4 */}
                  {/* 5 */}
                  <div className="grid grid-cols-[154px_194px] gap-x-[8px]">
                    <div>
                      <Input
                        {...register('settings.settings.exclude_begin_sec', {
                          required: { value: true, message: tf('required') },
                          valueAsNumber: true,
                        })}
                        type="number"
                        label="Исключить первые сек."
                        icon="ClockIcon"
                        error={errors.settings?.settings?.exclude_begin_sec?.message}
                      />
                    </div>
                    <div>
                      <Input
                        {...register('settings.settings.exclude_end_sec', {
                          required: { value: true, message: tf('required') },
                          valueAsNumber: true,
                        })}
                        type="number"
                        label="Исключить последние сек."
                        icon="ClockIcon"
                        error={errors.settings?.settings?.exclude_end_sec?.message}
                      />
                    </div>
                  </div>
                  {/* 5 */}
                  {/* 6 */}
                  <div className="grid grid-cols-[163px_185px] gap-x-[8px]">
                    <div>
                      <Input
                        {...register('settings.settings.search_begin_sec', {
                          valueAsNumber: true,
                        })}
                        type="number"
                        label="Искать в первых сек."
                        icon="ClockIcon"
                      />
                    </div>
                    <div>
                      <Input
                        {...register('settings.settings.search_end_sec', {
                          valueAsNumber: true,
                        })}
                        type="number"
                        label="Искать в последних сек."
                        icon="ClockIcon"
                      />
                    </div>
                  </div>
                  {/* 6 */}
                </div>
                {/* GRID END */}
              </GridWrapper>
            </SimpleAccordion>
            {/* hidden submit */}
            <button ref={hiddenButRef} className="hidden" type="submit" />
            {/* hidden submit */}
          </div>
        </div>
      </form>
      {isLoading ? (
        <Skeleton />
      ) : (
        (editModal || copyModal) && (
          <AiMetricIndividualMetric
            currentTemplate={currentTemplate}
            metricTemplates={metricTemplates}
            currentMetricsDataFromGroup={currentMetricsDataFromGroup}
            gptModalData={gptModalData}
            gpt_metrics={
              gptGroupsList?.gpt_groups.filter(
                (group) => group.gpt_group_id !== gptModalData?.gpt_group_id,
              ) || []
            }
            voiceMetricList={metricList}
          />
        )
      )}
      {/* BUTTONSBLOCK */}
      <div className="flex items-center justify-between py-[24px]  sticky bottom-0 bg-white">
        <div>
          <Button onClick={() => modalsSetState('gpt')} fill="linked" label="Отменить" />
        </div>
        <div className="flex items-center gap-[16px]">
          {/* <Button fill="linked" icon="FolderPlusIcon" label="Сохранить как шаблон" /> */}
          {/* <Button fill="linked" icon="FolderIcon" label="Загрузить из шаблона" /> */}
          <Button
            label="Протестировать"
            onClick={handleOpenTestModal}
            fill="outlined"
            sizeIcon={12}
            icon="TriangleEmptyIcon"
            disabled={!currentMetricsDataFromGroup?.length}
          />
          <Button
            disabled={status?.project_status === AllStatusProject.ACTIVE}
            onClick={() => hiddenButRef?.current?.click()}
            label="Сохранить"
          />
        </div>
      </div>
      {/* BUTTONSBLOCK */}
      {/* TESTING MODAL */}
      <EasyModal
        show={testModalOpenState}
        variant="largeW1164"
        withoutFooter
        withoutContentPadding
        onClose={onCloseModal}
        label={testModalLoading ? testModalLabelDuringLoading : testingModalLabels[step]}
      >
        {testModalOpenState && (
          <AiMetricTestingModal
            loading={testModalLoading}
            setLoading={setTestModalLoading}
            setStep={setStep}
            step={step}
            onCloseModal={onCloseModal}
            modelsOptions={modelsOptions}
            group={gptModalData}
            channels={[
              { name: 'Все', settings_channels_id: 'all', channel: 99999 },
              ...(allChannels || []),
            ]}
            gpt_metrics={gptGroupsList?.gpt_groups || []}
            voiceMetricList={metricList}
            currentMetricsDataFromGroup={currentMetricsDataFromGroup}
            testMetric={testMetric}
            currentData={currentData}
            currentTemplate={'voice'}
          />
        )}
      </EasyModal>
      {/* TESTING MODAL */}
    </div>
  );
};

export default AiMetricVoice;
